import React, { useState, useEffect, useRef } from 'react';
import { Modal, Button, Select, Space, Tag, Empty, Spin, List, Typography, message, Card, Divider, Progress } from 'antd';
import { 
  PlayCircleOutlined, 
  StopOutlined, 
  ReloadOutlined, 
  ClearOutlined,
  FileTextOutlined,
  InfoCircleOutlined, 
  ExclamationCircleOutlined, 
  CloseCircleOutlined,
  DownloadOutlined
} from '@ant-design/icons';

const { Option } = Select;
const API_URL = process.env.REACT_APP_API_URL || '';

// Progress calculation constants based on typical test execution
const PROGRESS_THRESHOLDS = {
  MIN_LOGS_SUCCESS: 142,
  MAX_LOGS_SUCCESS: 153,
  EXPECTED_LOGS: 147.5, // Average of min and max
  EARLY_STAGE: 30,      // Setup and initialization
  MID_STAGE: 75,        // Main testing phase  
  LATE_STAGE: 120,      // Cleanup and results
};

function LogModal({ executionId, onClose }) {
  const [logs, setLogs] = useState([]);
  const [isStreaming, setIsStreaming] = useState(false);
  const [levelFilter, setLevelFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentProgress, setCurrentProgress] = useState(null);
  const eventSourceRef = useRef(null);
  const logsContainerRef = useRef(null);

  // Load existing logs
  const loadLogs = async () => {
    if (!executionId) return;
    
    setIsLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/logs/${executionId}`);
      const data = await response.json();
      setLogs(data.logs || []);
      
      // Set initial progress if available from server
      if (data.progress) {
        setCurrentProgress(data.progress);
      } else {
        // Fallback to local calculation
        const progress = calculateProgress(data.logs?.length || 0);
        setCurrentProgress(progress);
      }
    } catch (error) {
      console.error('Failed to load logs:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Start streaming logs
  const startStreaming = () => {
    if (!executionId || isStreaming) return;

    const eventSource = new EventSource(`${API_URL}/api/logs/${executionId}/stream`);
    eventSourceRef.current = eventSource;

    eventSource.onopen = () => {
      setIsStreaming(true);
    };

    eventSource.onmessage = (event) => {
      const data = JSON.parse(event.data);
      
      if (data.type === 'progress_update') {
        // Handle progress updates
        setCurrentProgress(data.progress);
      } else {
        // Handle regular log entries
        setLogs(prevLogs => [...prevLogs, data]);
        
        // Update progress based on log count if no server progress available
        if (!currentProgress) {
          const newProgress = calculateProgress(logs.length + 1);
          setCurrentProgress(newProgress);
        }
      }
    };

    eventSource.onerror = () => {
      setIsStreaming(false);
      eventSource.close();
    };
  };

  // Stop streaming logs
  const stopStreaming = () => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
      setIsStreaming(false);
    }
  };

  // Clear logs display
  const clearLogs = () => {
    setLogs([]);
  };

  // Filter logs based on level and type
  const filteredLogs = logs.filter(log => {
    const levelPriority = { debug: 0, info: 1, warn: 2, error: 3 };
    const minLevel = levelFilter ? levelPriority[levelFilter] : -1;
    
    const levelMatch = minLevel === -1 || levelPriority[log.level] >= minLevel;
    const typeMatch = !typeFilter || log.type === typeFilter;
    
    return levelMatch && typeMatch;
  });

  // Get log level color
  const getLogLevelColor = (level) => {
    switch (level) {
      case 'error': return '#ff4d4f';
      case 'warn': return '#faad14';
      case 'info': return '#1890ff';
      case 'debug': return '#52c41a';
      default: return '#d9d9d9';
    }
  };

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString();
  };

  // Calculate progress based on log count
  const calculateProgress = (logCount) => {
    if (logCount === 0) return { percent: 0, status: 'normal', stage: 'Not Started' };
    
    const percent = Math.min(100, (logCount / PROGRESS_THRESHOLDS.EXPECTED_LOGS) * 100);
    
    let status = 'active';
    let stage = '';
    
    if (logCount <= PROGRESS_THRESHOLDS.EARLY_STAGE) {
      stage = 'Initializing';
    } else if (logCount <= PROGRESS_THRESHOLDS.MID_STAGE) {
      stage = 'Testing';
    } else if (logCount <= PROGRESS_THRESHOLDS.LATE_STAGE) {
      stage = 'Processing';
    } else if (logCount >= PROGRESS_THRESHOLDS.MIN_LOGS_SUCCESS && 
               logCount <= PROGRESS_THRESHOLDS.MAX_LOGS_SUCCESS) {
      stage = 'Completing';
      status = 'success';
    } else if (logCount > PROGRESS_THRESHOLDS.MAX_LOGS_SUCCESS) {
      stage = 'Extended Run';
      status = 'exception';
    } else {
      stage = 'Finalizing';
    }
    
    return { percent: Math.round(percent), status, stage };
  };

  // Check if execution appears to be completed based on logs
  const isExecutionComplete = (logCount, lastLogTime) => {
    const now = Date.now();
    const lastActivity = new Date(lastLogTime).getTime();
    const timeSinceLastLog = now - lastActivity;
    
    // Consider complete if:
    // 1. Log count is in success range (142-153)
    // 2. No new logs for more than 30 seconds
    return (
      logCount >= PROGRESS_THRESHOLDS.MIN_LOGS_SUCCESS &&
      logCount <= PROGRESS_THRESHOLDS.MAX_LOGS_SUCCESS &&
      timeSinceLastLog > 30000
    ) || (
      // Or if logs stopped coming and we have substantial progress
      logCount > PROGRESS_THRESHOLDS.LATE_STAGE &&
      timeSinceLastLog > 60000
    );
  };

  // Auto-scroll to bottom when new logs arrive
  useEffect(() => {
    if (logsContainerRef.current) {
      logsContainerRef.current.scrollTop = logsContainerRef.current.scrollHeight;
    }
  }, [filteredLogs]);

  // Load logs on mount
  useEffect(() => {
    loadLogs();
    return () => stopStreaming();
  }, [executionId]);

  const availableTypes = [...new Set(logs.map(log => log.type).filter(Boolean))];

  return (
    <Modal
      title={
        <Space>
          <FileTextOutlined />
          <span>Logs for Execution: {executionId}</span>
        </Space>
      }
      open={true}
      onCancel={onClose}
      width="90vw"
      style={{ top: 20 }}
      styles={{ height: '70vh', padding: 0 }}
      footer={null}
    >
      {/* Controls */}
      <div style={{ 
        padding: '16px', 
        borderBottom: '1px solid #f0f0f0',
        background: '#fafafa'
      }}>
        <Space wrap>
          <Space>
            <Button 
              type={isStreaming ? "default" : "primary"}
              icon={isStreaming ? <StopOutlined /> : <PlayCircleOutlined />}
              onClick={isStreaming ? stopStreaming : startStreaming}
              disabled={isLoading}
              danger={isStreaming}
            >
              {isStreaming ? 'Stop Stream' : 'Start Stream'}
            </Button>
            
            <Button 
              icon={<ReloadOutlined />}
              onClick={loadLogs}
              disabled={isLoading}
            >
              Refresh
            </Button>
            
            <Button 
              icon={<ClearOutlined />}
              onClick={clearLogs}
            >
              Clear Display
            </Button>
          </Space>

          <Space>
            <Select 
              value={levelFilter} 
              onChange={setLevelFilter}
              placeholder="Filter by level"
              style={{ width: 150 }}
              allowClear
            >
              <Option value="error">Errors Only</Option>
              <Option value="warn">Warnings & Errors</Option>
              <Option value="info">Info & Above</Option>
              <Option value="debug">Debug & Above</Option>
            </Select>

            <Select 
              value={typeFilter} 
              onChange={setTypeFilter}
              placeholder="Filter by type"
              style={{ width: 150 }}
              allowClear
            >
              {availableTypes.map(type => (
                <Option key={type} value={type}>{type}</Option>
              ))}
            </Select>
          </Space>
        </Space>

        <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Space>
            <Tag color={isStreaming ? 'red' : 'default'}>
              {isStreaming ? '🔴 Live Streaming' : '⚫ Stopped'}
            </Tag>
            <span style={{ fontSize: '12px', color: '#666' }}>
              Showing {filteredLogs.length} of {logs.length} logs
            </span>
          </Space>
        </div>

        {/* Progress indicator */}
        {logs.length > 0 && (() => {
          // Use real-time progress from server if available, otherwise calculate locally
          const progress = currentProgress || calculateProgress(logs.length);
          const lastLogTime = logs.length > 0 ? logs[logs.length - 1].timestamp : null;
          const isComplete = lastLogTime ? isExecutionComplete(logs.length, lastLogTime) : false;
          
          return (
            <div style={{ marginTop: '12px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                <span style={{ fontSize: '12px', fontWeight: 'bold', color: '#333' }}>
                  Test Progress: {progress.stage}
                  {currentProgress && (
                    <Tag color="blue" size="small" style={{ marginLeft: '8px' }}>
                      🔴 Live
                    </Tag>
                  )}
                </span>
                <Space size="small">
                  <span style={{ fontSize: '12px', color: '#666' }}>
                    {currentProgress?.totalLogs || logs.length}/{PROGRESS_THRESHOLDS.EXPECTED_LOGS} logs
                  </span>
                  {(isComplete || progress.status === 'success') && (
                    <Tag color="success" size="small">
                      ✅ Complete
                    </Tag>
                  )}
                </Space>
              </div>
              <Progress 
                percent={progress.percent} 
                status={isComplete || progress.status === 'success' ? 'success' : progress.status}
                size="small"
                strokeColor={
                  progress.status === 'success' ? '#52c41a' :
                  progress.status === 'exception' ? '#faad14' : 
                  '#1890ff'
                }
                showInfo={false}
              />
              <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '2px' }}>
                <span style={{ fontSize: '10px', color: '#999' }}>
                  Expected: {PROGRESS_THRESHOLDS.MIN_LOGS_SUCCESS}-{PROGRESS_THRESHOLDS.MAX_LOGS_SUCCESS} logs
                </span>
                <span style={{ fontSize: '10px', color: '#999' }}>
                  {progress.percent}% {currentProgress ? '(Real-time)' : '(Estimated)'}
                </span>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Logs Container */}
      <div 
        ref={logsContainerRef}
        style={{ 
          height: 'calc(100% - 100px)',
          overflow: 'auto',
          padding: '16px',
          background: '#1e1e1e',
          color: '#d4d4d4',
          fontFamily: 'Consolas, Monaco, "Courier New", monospace',
          fontSize: '12px'
        }}
      >
        {isLoading ? (
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            height: '200px' 
          }}>
            <Spin size="large" />
            <span style={{ marginLeft: '12px' }}>Loading logs...</span>
          </div>
        ) : filteredLogs.length === 0 ? (
          <Empty 
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={
              <span style={{ color: '#d4d4d4' }}>
                {logs.length > 0 
                  ? 'No logs match the current filters' 
                  : 'No logs found for this execution'
                }
              </span>
            }
          />
        ) : (
          filteredLogs.map((log, index) => (
            <div 
              key={index} 
              style={{
                marginBottom: '4px',
                padding: '4px 8px',
                borderLeft: `3px solid ${getLogLevelColor(log.level)}`,
                borderRadius: '2px',
                display: 'flex',
                gap: '10px',
                alignItems: 'flex-start',
                backgroundColor: log.level === 'error' ? 'rgba(241, 76, 76, 0.1)' :
                                log.level === 'warn' ? 'rgba(220, 220, 170, 0.1)' : 
                                'transparent'
              }}
            >
              <span style={{ 
                color: '#808080', 
                fontSize: '11px',
                minWidth: '70px',
                flexShrink: 0
              }}>
                {formatTimestamp(log.timestamp)}
              </span>
              
              <span style={{ 
                color: getLogLevelColor(log.level),
                fontSize: '11px',
                fontWeight: 'bold',
                minWidth: '45px',
                flexShrink: 0,
                textTransform: 'uppercase'
              }}>
                {log.level}
              </span>
              
              {log.type && (
                <span style={{
                  color: '#9cdcfe',
                  fontSize: '11px',
                  background: 'rgba(156, 220, 254, 0.2)',
                  padding: '1px 4px',
                  borderRadius: '2px',
                  flexShrink: 0
                }}>
                  [{log.type}]
                </span>
              )}
              
              <span style={{ 
                whiteSpace: 'pre-wrap',
                flex: 1,
                wordBreak: 'break-word'
              }}>
                {log.message}
              </span>
            </div>
          ))
        )}
      </div>
    </Modal>
  );
}

export default LogModal; 